import { useState, useEffect, useCallback, useRef } from "react";
import { BASE_URL } from "../../../../../utilFunctions/Constants/urlConstants";
import { useGetApi } from "../../../../../utilFunctions/CustomHooks/useGetAPI";
import { IUserInfo } from "../../../../../utilFunctions/CommonInterfaces/userInterface";
import { Comment } from "./ChannelCommentHome";
import {
  deleteAPI,
  getAPIRequest,
} from "../../../../../utilFunctions/Axios/AxiosRequestConfiguration";
import { message } from "antd";

import { usePutPostAPI } from "../../../../../utilFunctions/CustomHooks/usePutPostAPI";

export const useComments = (
  postBatchId: number,
  user: IUserInfo,
  refreshTrigger: number,
  onCommentCreated: (response: any, payload: any) => void,
  onCommentsFetched?: (data: any) => void
) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingRepliesFor, setLoadingRepliesFor] = useState<string | null>(
    null
  );
  const offset = useRef(0);
  const LIMIT = 10;

  const { makeRequest: fetchComments, loading } = useGetApi(
    (data) => {
      if (data?.data) {
        const newComments = (data.data || []).map((c: any) => {
          const { author = {} } = c;
          const isOwn =
            String(c.createdBy ?? "") === String(user.id ?? "") ||
            String(author.id ?? "") === String(user.id ?? "");
          return {
            id: c.commentId,
            author: {
              id: author.id,
              name: author.id == user.id ? user.user_name : author.name,
              role: author.id == user.id ? user.fexRole : author.role,
              imageUrl: author.imageUrl,
            },
            timestamp: c.createdAt,
            content: c.text,
            isOwnPost: isOwn,
            replies: [], // Initialize replies as empty
            replyCount: c.replyCount || 0,
            parentCommentId: c.parentCommentId,
            hasMoreReplies: (c.replyCount || 0) > 0,
          };
        });
        if (offset.current === 0) {
          setComments(newComments);
        } else {
          setComments((prev) => [...prev, ...newComments]);
        }
        setHasMore(newComments.length >= LIMIT);
        if (onCommentsFetched) {
          onCommentsFetched(data);
        }
      } else {
        setHasMore(false);
        if (offset.current === 0) {
          setComments([]);
        }
      }
    },
    (err: any) => {
      console.error("Error fetching comments:", err);
      if (offset.current === 0) {
        setComments([]);
      }
      setHasMore(false);
    }
  );

  const { makeRequest: sendComment, loading: isSubmitting } = usePutPostAPI(
    (response, payload) => {
      message.success("Comment posted successfully!");
      if (onCommentCreated) {
        onCommentCreated(response, payload);
      }
    },
    () => message.error("Failed to post comment."),
    "POST"
  );

  const { makeRequest: editComment } = usePutPostAPI(
    (response: any, payload: any) => {
      message.success("Comment updated successfully!");
      setComments((prevComments) =>
        prevComments.map((comment) => {
          if (comment.id === payload.commentId) {
            return { ...comment, content: payload.text };
          }
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: comment.replies.map((reply) => {
                if (reply.id === payload.commentId) {
                  return { ...reply, content: payload.text };
                }
                return reply;
              }),
            };
          }
          return comment;
        })
      );
    },
    () => message.error("Failed to update comment."),
    "PUT"
  );

  const deleteCommentRequest = (commentId: string, commentData?: any) => {
    const url = `${BASE_URL}postComment/` + commentId;
    deleteAPI({
      url: url,
      success: () => {
        message.success("Comment deleted successfully!");
        const currentOffset = offset.current;
        const url = `${BASE_URL}postComment?postBatchId=${postBatchId}&offset=${currentOffset}&limit=${LIMIT}&createdBy=${user.id}`;
        stableFetchComments.current(url);
        if (commentData?.onDeleteSuccess) {
          commentData.onDeleteSuccess();
        }
      },
      failure: (error: any) => {
        message.error(
          error.response?.data?.message || "Failed to delete comment"
        );
      },
    });
  };

  const createComment = (
    text: string,
    postBatchId: number,
    userId: number,
    parentCommentId: string | null = null
  ) => {
    const url = BASE_URL + "postComment";
    const payload = {
      text,
      postBatchId,
      createdBy: userId,
      parentCommentId,
    };
    sendComment(url, payload);
  };

  const updateComment = (text: string, commentId: string, userId: number) => {
    const url = `${BASE_URL}postComment`;
    const payload = { text: text, commentId: commentId, createdBy: userId };
    editComment(url, payload);
  };

  const deleteComment = (commentId: string, commentData?: any) => {
    deleteCommentRequest(commentId, commentData);
  };

  const stableFetchComments = useRef(fetchComments);
  useEffect(() => {
    stableFetchComments.current = fetchComments;
  }, [fetchComments]);

  const loadComments = useCallback(
    (isRefresh = false) => {
      let currentOffset = offset.current;
      if (isRefresh) {
        currentOffset = 0;
        offset.current = 0;
      }
      if (postBatchId && user.id) {
        const url = `${BASE_URL}postComment?postBatchId=${postBatchId}&offset=${currentOffset}&limit=${LIMIT}&createdBy=${user.id}`;
        stableFetchComments.current(url);
      }
    },
    [postBatchId, user.id]
  );

  const commentsRef = useRef(comments);
  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  const findCommentById = (
    commentList: Comment[],
    targetId: string
  ): Comment | undefined => {
    for (const c of commentList) {
      if (c.id === targetId) return c;
      if (c.replies && c.replies.length > 0) {
        const found = findCommentById(c.replies, targetId);
        if (found) return found;
      }
    }
    return undefined;
  };

  const updateRepliesInComments = (
    commentList: Comment[],
    targetParentId: string,
    newReplies: Comment[],
    isNext: boolean,
    limit: number
  ): Comment[] => {
    return commentList.map((c) => {
      if (c.id === targetParentId) {
        const updatedReplies = isNext
          ? [...(c.replies || []), ...newReplies]
          : newReplies;
        return {
          ...c,
          replies: updatedReplies,
          hasMoreReplies: newReplies.length >= limit,
        };
      }
      if (c.replies && c.replies.length > 0) {
        return {
          ...c,
          replies: updateRepliesInComments(
            c.replies,
            targetParentId,
            newReplies,
            isNext,
            limit
          ),
        };
      }
      return c;
    });
  };

  const fetchReplies = (parentId: string, isNext = false) => {
    if (loadingRepliesFor === parentId) return;

    let currentOffset = 0;
    if (isNext) {
      const parentComment = findCommentById(commentsRef.current, parentId);
      currentOffset = parentComment?.replies?.length || 0;
    }

    setLoadingRepliesFor(parentId);
    const url = `${BASE_URL}postComment/getReply?postBatchId=${postBatchId}&offset=${currentOffset}&limit=${LIMIT}&createdBy=${user.id}&parentCommentId=${parentId}`;

    getAPIRequest({
      url,
      success: (response: any) => {
        const repliesData = response.data.data || [];
        const mappedReplies = repliesData.map((r: any) => {
          const { author = {} } = r;
          return {
            id: r.commentId,
            author: {
              id: author.id,
              name: author.name,
              role: author.role,
              imageUrl: author.imageUrl,
            },
            timestamp: r.createdAt,
            content: r.text,
            isOwnPost: String(r.author.id ?? "") === String(user.id ?? ""),
            replies: [],
            replyCount: r.replyCount || 0,
            parentCommentId: r.parentCommentId,
            hasMoreReplies: (r.replyCount || 0) > 0,
          };
        });

        setComments((prevComments) =>
          updateRepliesInComments(
            prevComments,
            parentId,
            mappedReplies,
            isNext,
            LIMIT
          )
        );
        setLoadingRepliesFor(null);
      },
      failure: (error: any) => {
        message.error("Failed to load replies.");
        console.error(`Error fetching replies for comment ${parentId}:`, error);
        setLoadingRepliesFor(null);
      },
    });
  };

  const next = () => {
    if (loading) return;
    offset.current += LIMIT;
    loadComments();
  };

  useEffect(() => {
    loadComments(true);
  }, [loadComments, refreshTrigger]);

  return {
    comments,
    setComments,
    loading,
    loadingRepliesFor,
    hasMore,
    next,
    refresh: () => loadComments(true),
    fetchReplies,
    createComment,
    updateComment,
    deleteComment,
    isSubmitting,
  };
};
